import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Edit2, Check, CircleMinus, CircleCheck, Lock, FileText, ChevronRight, ClipboardList } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import { Authority } from "./AuthorityTypes";
import { ToastMessage } from "../common/Toast";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea } from "../../validation/ValidatedFields";
import {
  governanceStakeholdersService,
  type AuthoritySop,
} from "../../services/governanceService";
import AuthoritySopInvolvementModal, { roleMeta } from "./AuthoritySopInvolvementModal";
import { statusVariant } from "../../utils/statusVariant";

type Tab = "basic" | "sops";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  authority: Authority | null;
  /** Full list, used to resolve the parent name + populate "Falls under" + cycle/dup checks. */
  authorities: Authority[];
  canModify: boolean;
  /** Persists an edit. Resolves on success so the modal can drop back to view mode. */
  onSubmit: (data: any) => Promise<void>;
  /** Toggles is_active on the current record (parent confirms + persists). */
  onToggleActive?: () => Promise<void>;
  setToast?: (toast: ToastMessage) => void;
}

/**
 * Single modal for an existing authority — mirrors the Contracts CNT-15 pattern:
 * it opens read-only and the footer's Edit button unlocks the Basic-details form
 * IN PLACE (no second modal pops up). Two tabs:
 *  1. Basic details — locked-until-Edit form (name / type / parent / description).
 *  2. SOPs — every SOP this authority is a stakeholder in; clicking one opens a
 *     small nested modal showing the exact step(s) + capacity (role).
 * Creating a brand-new authority still uses AuthorityForm (a distinct "Create" flow).
 */
const AuthorityDetailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  authority,
  authorities,
  canModify,
  onSubmit,
  onToggleActive,
  setToast,
}) => {
  const [tab, setTab] = useState<Tab>("basic");
  const [sops, setSops] = useState<AuthoritySop[]>([]);
  const [loadingSops, setLoadingSops] = useState(false);
  const [loadedForId, setLoadedForId] = useState<number | null>(null);
  const [selectedSop, setSelectedSop] = useState<AuthoritySop | null>(null);

  // View-first edit state (the Contracts pattern). Locked until the user clicks Edit.
  const isApproved = authority?.workflow_state === "approved";
  const [editingBasic, setEditingBasic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const fieldsDisabled = !canModify || isApproved || !editingBasic;
  const fieldVh = useFieldValidation();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_authority_id: null as number | null,
    is_physical: true,
  });

  // Reset everything when the modal opens for a (possibly different) authority.
  useEffect(() => {
    if (!isOpen) return;
    setTab("basic");
    setSops([]);
    setLoadedForId(null);
    setSelectedSop(null);
    setEditingBasic(false);
    setSubmitting(false);
    setToggling(false);
    fieldVh.reset();
    if (authority) {
      setFormData({
        name: authority.name,
        description: authority.description || "",
        parent_authority_id: authority.parent_authority_id,
        is_physical: authority.is_physical,
      });
    }
  }, [isOpen, authority?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // IDs of the current node + all descendants — none can be its parent (cycle guard).
  const forbiddenParentIds = useMemo(() => {
    const forbidden = new Set<number>();
    if (!authority) return forbidden;
    forbidden.add(authority.id);
    const stack = [authority.id];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const a of authorities) {
        if (a.parent_authority_id === cur && !forbidden.has(a.id)) {
          forbidden.add(a.id);
          stack.push(a.id);
        }
      }
    }
    return forbidden;
  }, [authority, authorities]);

  const runDomainChecks = (): boolean => {
    let ok = true;
    const name = formData.name.trim();
    if (name) {
      const dup = authorities.find(
        (a) => a.id !== authority?.id && a.is_active !== -1 &&
          a.name.trim().toLowerCase() === name.toLowerCase(),
      );
      if (dup) { fieldVh.setError("name", "An authority with this name already exists in this organisation."); ok = false; }
    }
    if (formData.parent_authority_id != null && authority && forbiddenParentIds.has(formData.parent_authority_id)) {
      fieldVh.setError("parent_authority_id", formData.parent_authority_id === authority.id
        ? "An authority cannot be its own parent."
        : "Cannot select a descendant — that would create a hierarchy cycle.");
      ok = false;
    }
    return ok;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const engineOk = fieldVh.validateAll([
      { name: "name", code: 109, value: formData.name, required: true },
      { name: "description", code: 110, value: formData.description },
    ]);
    const domainOk = runDomainChecks();
    if (!engineOk || !domainOk) return;
    setSubmitting(true);
    try {
      await onSubmit({ ...formData, name: formData.name.trim() });
      setEditingBasic(false); // back to view mode on success
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!onToggleActive || isApproved) return;
    setToggling(true);
    try {
      await onToggleActive();
    } finally {
      setToggling(false);
    }
  };

  const loadSops = useCallback(async (authorityId: number) => {
    setLoadingSops(true);
    try {
      const data = await governanceStakeholdersService.getAuthoritySops(authorityId);
      setSops(data);
      setLoadedForId(authorityId);
    } catch {
      setToast?.({ type: "error", message: "Failed to load SOPs." });
    } finally {
      setLoadingSops(false);
    }
  }, [setToast]);

  // Lazy-load the SOPs the first time the tab is opened for this authority.
  useEffect(() => {
    if (!isOpen || tab !== "sops" || !authority) return;
    if (loadedForId === authority.id || loadingSops) return;
    loadSops(authority.id);
  }, [isOpen, tab, authority, loadedForId, loadingSops, loadSops]);

  if (!authority) return null;

  const sopCount = loadedForId === authority.id ? sops.length : null;
  const showToggle = !!onToggleActive && !isApproved;
  const splitFooter = showToggle;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Header>
          {isApproved && <Lock className="lock-icon lock-icon--md" style={{ marginRight: 6 }} />}
          "<span className="item-highlight">{authority.name}</span>"
          {authority.code && (
            <code className="ml-2 bg-slate-100 px-1.5 py-0.5 rounded text-xs text-gray-500">
              {authority.code}
            </code>
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="app-modal-tabs">
            <button
              type="button"
              className={`app-modal-tab${tab === "basic" ? " app-modal-tab--active" : ""}`}
              onClick={() => setTab("basic")}
              data-testid="TAB_authority_detail_basic"
            >
              Basic details
            </button>
            <button
              type="button"
              className={`app-modal-tab${tab === "sops" ? " app-modal-tab--active" : ""}`}
              onClick={() => setTab("sops")}
              data-testid="TAB_authority_detail_sops"
            >
              <ClipboardList size={14} />
              SOPs
              {sopCount != null && <span className="app-modal-tab-badge">{sopCount}</span>}
            </button>
          </div>

          {tab === "basic" ? (
            <div data-testid="PANEL_authority_detail_basic">
              {isApproved && (
                <div className="approved-banner" data-testid="BANNER_authority_detail_locked">
                  <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                </div>
              )}
              <form
                id="authority-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-y-4"
              >
                <FormControl.Input
                  label="Code"
                  value={authority.code || "—"}
                  disabled
                  data-testid="INP_authority_form_code"
                />
                <ValidatedInput
                  vh={fieldVh}
                  name="name"
                  code={109}
                  required
                  label="Authority Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Board of Directors"
                  disabled={fieldsDisabled}
                  data-testid="INP_authority_form_name"
                />

                <div className="grid grid-cols-2 gap-4">
                  <div
                    onClick={() => !fieldsDisabled && setFormData({ ...formData, is_physical: true })}
                    data-testid="RAD_authority_form_type_physical"
                    className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${fieldsDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${formData.is_physical ? "bg-blue-50 border-blue-300" : "hover:bg-slate-50 border-slate-200"}`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${formData.is_physical ? "border-blue-600" : "border-slate-400"}`}>
                      {formData.is_physical && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-slate-900">Physical</span>
                      <span className="block text-xs text-slate-500">Physical governing body.</span>
                    </div>
                  </div>

                  <div
                    onClick={() => !fieldsDisabled && setFormData({ ...formData, is_physical: false })}
                    data-testid="RAD_authority_form_type_logical"
                    className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${fieldsDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${!formData.is_physical ? "bg-emerald-50 border-emerald-300" : "hover:bg-slate-50 border-slate-200"}`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${!formData.is_physical ? "border-emerald-600" : "border-slate-400"}`}>
                      {!formData.is_physical && <div className="w-2 h-2 bg-emerald-600 rounded-full" />}
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-slate-900">Logical</span>
                      <span className="block text-xs text-slate-500">Functional/Logical authority.</span>
                    </div>
                  </div>
                </div>

                <FormControl.SearchSelect
                  label="Falls Under"
                  data-testid="SEL_authority_form_parent"
                  value={formData.parent_authority_id}
                  onChange={(value) => {
                    if (fieldsDisabled) return;
                    setFormData({ ...formData, parent_authority_id: value ? Number(value) : null });
                    fieldVh.clearError("parent_authority_id");
                  }}
                  options={authorities
                    .filter(a => !forbiddenParentIds.has(a.id))
                    .map(a => ({ value: a.id, label: a.name }))}
                  placeholder="-- None (Top Level) --"
                  clearable
                  error={fieldVh.errors.parent_authority_id}
                  disabled={fieldsDisabled}
                />

                <ValidatedTextarea
                  vh={fieldVh}
                  name="description"
                  code={110}
                  label="Description (Optional)"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of responsibilities..."
                  disabled={fieldsDisabled}
                  data-testid="INP_authority_form_description"
                />
              </form>
            </div>
          ) : (
            <div data-testid="PANEL_authority_detail_sops">
              {loadingSops ? (
                <div className="flex flex-col gap-2" aria-busy="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : sops.length === 0 ? (
                <div className="text-center py-12 text-gray-400" data-testid="EMPTY_authority_detail_sops">
                  <ClipboardList size={32} className="mx-auto opacity-20 mb-2" />
                  <p className="text-sm">Not a stakeholder in any SOP yet.</p>
                  <p className="text-xs mt-1">SOPs where this authority has a step role appear here.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {sops.map((sop, idx) => (
                    <button
                      type="button"
                      key={sop.process_id}
                      onClick={() => setSelectedSop(sop)}
                      data-testid={`ROW_authority_detail_sop_${sop.process_id}`}
                      className="w-full text-left flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-200 hover:shadow-sm transition group"
                    >
                      <span className="w-6 text-center text-xs font-semibold text-gray-400 flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FileText size={15} className="text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{sop.process_name}</p>
                        <p className="text-[11px] text-gray-400">
                          {sop.process_code && <span className="mr-2">{sop.process_code}</span>}
                          {sop.step_count} step{sop.step_count !== 1 ? "s" : ""}
                        </p>
                      </div>
                      {/* Maker-checker workflow_state (matches ApprovalDot), not
                          legacy process_status which lies after a reopen. */}
                      <span className={`status-badge xs ${statusVariant(sop.workflow_state)}`}>
                        {sop.workflow_state}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {sop.roles.map((r) => {
                          const rm = roleMeta(r);
                          return (
                            <span key={r} className={`status-badge xs ${rm.badge}`}>
                              {rm.label}
                            </span>
                          );
                        })}
                      </div>
                      <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 transition flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        {/* Footer — Basic tab only. Inactive toggle (left) + Edit→Update (right). */}
        {tab === "basic" && (
          <Modal.Footer className={splitFooter ? "app-modal-footer--split" : ""}>
            {showToggle && (
              <Button
                variant={authority.is_active ? "inactive-outline" : "active-outline"}
                icon={authority.is_active ? CircleMinus : CircleCheck}
                data-testid="BTN_authority_form_toggle_active"
                onClick={handleToggleActive}
                disabled={toggling || submitting}
              >
                {toggling ? "..." : authority.is_active ? "Inactive" : "Active"}
              </Button>
            )}
            {canModify && !isApproved && !editingBasic && (
              <Button
                variant="fill"
                icon={Edit2}
                data-testid="BTN_authority_detail_edit"
                onClick={() => setEditingBasic(true)}
                disabled={toggling}
              >
                Edit
              </Button>
            )}
            {canModify && !isApproved && editingBasic && (
              <Button
                type="submit"
                form="authority-form"
                variant="update"
                icon={Check}
                data-testid="BTN_authority_form_submit"
                disabled={submitting || toggling}
              >
                {submitting ? "Updating..." : "Update"}
              </Button>
            )}
          </Modal.Footer>
        )}
      </Modal>

      <AuthoritySopInvolvementModal
        isOpen={!!selectedSop}
        onClose={() => setSelectedSop(null)}
        sop={selectedSop}
        authorityName={authority.name}
      />
    </>
  );
};

export default AuthorityDetailModal;
